import { F0Dialog, f0FormField, useF0Form, useF0FormDefinition } from '@factorialco/f0-react'
import {
  DuplicateProcurementTypeDocument,
  ProcurementTypeIdentityFragment,
  ProcurementTypesDataCollectionDocument,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { FactorialF0Form } from 'modules/core/components/FactorialF0Form'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  onClose: () => void
  procurementType: ProcurementTypeIdentityFragment
}

const DuplicateRequestTypeModal = ({ onClose, procurementType }: Props) => {
  const { employee } = useCurrentSession()

  const [duplicateProcurementType] = useMutation(DuplicateProcurementTypeDocument, {
    extractorFn: (data) => data.procurementMutations.duplicateProcurementType,
  })

  const { formRef, submit, isSubmitting, hasErrors, isDirty } = useF0Form()

  const schema = z.object({
    duplicateName: f0FormField(z.string().trim().min(1), {
      label: i18n.t('procurement.purchase_requests.settings.types.modals.detail.labels.name'),
    }),
    duplicateDescription: f0FormField(z.string().trim().min(1), {
      label: i18n.t(
        'procurement.purchase_requests.settings.types.modals.detail.labels.description'
      ),
    }),
  })

  const formDefinition = useF0FormDefinition({
    name: 'duplicate-request-type',
    schema,
    defaultValues: { duplicateName: '', duplicateDescription: '' },
    onSubmit: async ({ data }) => {
      const result = await duplicateProcurementType({
        variables: {
          id: procurementType.id,
          duplicateName: data.duplicateName,
          duplicateDescription: data.duplicateDescription,
          authorId: employee.id,
        },
        refetchQueries: [ProcurementTypesDataCollectionDocument],
      })

      let fieldErrors: Record<string, string> = {}
      const handled = handleResult(result, (errors) => {
        fieldErrors = errors
      })

      if (!handled.success) {
        return { success: false, errors: fieldErrors }
      }

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      onClose()
      return { success: true }
    },
    submitConfig: { type: 'default', hideSubmitButton: true },
  })

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('procurement.purchase_requests.settings.types.modals.duplicate.title')}
      description={i18n.t(
        'procurement.purchase_requests.settings.types.modals.duplicate.description',
        { name: procurementType.name }
      )}
      primaryAction={{
        label: i18n.t('procurement.purchase_requests.settings.types.modals.duplicate.buttons.save'),
        onClick: submit,
        loading: isSubmitting,
        disabled: hasErrors || !isDirty,
      }}
      secondaryAction={{
        label: i18n.t(
          'procurement.purchase_requests.settings.types.modals.duplicate.buttons.cancel'
        ),
        onClick: onClose,
      }}
    >
      <FactorialF0Form formRef={formRef} formDefinition={formDefinition} />
    </F0Dialog>
  )
}

export default DuplicateRequestTypeModal
